import { useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { cn } from "@/lib/utils";

import type { Page } from "../../api";
import { FlagChip, FLAGS } from "../../chips";
import { OptionLink } from "../../links";
import { TabPage } from "../../PageFrame";
import type { PageProps } from "../index";
import { MIN_STANDARD, MUTED_ROW, PAGE_SIZE, PagedTable, PageHead, sortParams, usePage, useTableSort, type TableSort } from "../kit";
import { pageExtra } from "../params";

export interface CriteriaRankingRow {
  option_id: string;
  option_name: string;
  item_id: string;
  item_name: string;
  phase: string | null;
  dropped: boolean;
  feature: string | null;
  criterion: string | null;
  question: string | null;
  rating: number | null;
  comment: string | null;
  sample?: string | null;
  evaluator?: string | null;
  criteria_phase: string | null;
  not_scored: boolean;
}

/** How much of a question shows before it is opened. */
export const QUESTION_PREVIEW = 48;

/** A long question shows its first 48 characters; a click opens the rest in place. */
export function Question({ text }: { text: string | null }) {
  if (!text) return null;
  if (text.length <= QUESTION_PREVIEW) return <>{text}</>;
  return (
    <details>
      <summary className="cursor-pointer list-none">{text.slice(0, QUESTION_PREVIEW)}…</summary>
      {text}
    </details>
  );
}

/** An option with no rating has one row: the chip and its sentence run across the empty cells. */
const unscored = (row: CriteriaRankingRow) => row.criterion === null;

export const CRITERIA_RANKING_COLUMNS: ColumnDef<CriteriaRankingRow, unknown>[] = [
  {
    id: "option",
    // The header button now shows the current sort, so the fixed arrow is gone (Ioan 2026-10-16).
    header: "Option",
    meta: { sortable: true },
    cell: ({ row }) => (
      <OptionLink optionId={row.original.option_id} itemId={row.original.item_id} from="criteria_ranking">
        {row.original.option_name}
      </OptionLink>
    ),
  },
  {
    id: "feature",
    header: "Feature",
    meta: { sortable: true },
    cell: ({ row }) =>
      unscored(row.original) ? (
        <div className="relative h-6">
          <div className="absolute top-0 left-0 flex items-center gap-2 whitespace-nowrap">
            <FlagChip kind="not_scored" /> {FLAGS.not_scored.tooltip}
          </div>
        </div>
      ) : (
        row.original.feature
      ),
  },
  { id: "criterion", header: "Criterion", meta: { sortable: true }, cell: ({ row }) => row.original.criterion },
  { id: "sample", header: "Sample", meta: { sortable: true }, cell: ({ row }) => (unscored(row.original) ? null : row.original.sample ?? null) },
  { id: "evaluator", header: "Reviewed by", meta: { sortable: true }, cell: ({ row }) => (unscored(row.original) ? null : row.original.evaluator ?? null) },
  { id: "question", header: "Question", meta: { sortable: true }, cell: ({ row }) => <Question text={row.original.question} /> },
  {
    id: "rating",
    header: "Rating",
    meta: { align: "right", sortable: true },
    cell: ({ row }) => {
      const rating = row.original.rating;
      if (rating == null) return row.original.criterion === null ? null : <FlagChip kind="clarify" />;
      return <span className={cn(rating < MIN_STANDARD && "font-bold text-ks-danger")}>{rating}</span>;
    },
  },
  { id: "comment", header: "Comment", meta: { sortable: true }, cell: ({ row }) => row.original.comment },
];

export function CriteriaRankingBody({ data, page, onPage, sort = null, onSort }: {
  data: Page<CriteriaRankingRow>; page: number; onPage: (next: number) => void; sort?: TableSort; onSort?: (id: string) => void;
}) {
  return (
    <PagedTable<CriteriaRankingRow>
      columns={CRITERIA_RANKING_COLUMNS}
      data={data}
      page={page}
      onPage={onPage}
      caption="Ratings and comments per option and criterion"
      wrap
      sort={sort}
      onSort={onSort}
      rowClassName={(row) => (row.dropped ? MUTED_ROW : undefined)}
    />
  );
}

export function CriteriaRankingPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const [page, setPage] = usePage();
  const [sort, onSort] = useTableSort();
  return (
    <TabPage<Page<CriteriaRankingRow>>
      tab={tab}
      areaName={areaName}
      extra={{ ...pageExtra(tab.tabKey, search), ...sortParams(sort), limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }}
      isEmpty={(data) => data.total === 0}
      head={(data) => {
        const first = data?.items[0];
        return (
          <PageHead
            title={tab.label}
            sub={first ? `${first.item_name} · click a question or comment to read it in full` : undefined}
          />
        );
      }}
    >
      {(data) => <CriteriaRankingBody data={data} page={page} onPage={setPage} sort={sort} onSort={onSort} />}
    </TabPage>
  );
}
